import {
  ConversationSession,
  UserInputItem,
  ProgressBlockItem,
  ClarificationRequestItem,
  ReportItem,
  FollowupQuestionItem,
  FollowupResponseItem,
  generateTimelineId,
} from '../../types/conversation';

/**
 * Test data for development - renders a complete conversation when user types "[test]"
 * Only works in development mode
 */

const baseTime = new Date();

function getTimeOffset(minutes: number): string {
  return new Date(baseTime.getTime() + minutes * 60 * 1000).toISOString();
}

const userInput: UserInputItem = {
  id: generateTimelineId(),
  type: 'user_input',
  createdAt: getTimeOffset(0),
  roundNumber: 1,
  content: 'Co wybrać umowę o pracę czy o dzieło? Chcę wiedzieć jakie są różnice i co jest korzystniejsze dla pracownika.',
};

const progressBlock1: ProgressBlockItem = {
  id: generateTimelineId(),
  type: 'progress_block',
  createdAt: getTimeOffset(0.5),
  roundNumber: 1,
  steps: [
    { type: 'progress', message: 'Analizuję zapytanie...', timestamp: getTimeOffset(0.5), completed: true },
    { type: 'progress', message: 'Przygotowuję pytania wyjaśniające...', timestamp: getTimeOffset(1), completed: true },
  ],
  phase: 'clarification',
  isComplete: true,
  isStreaming: false,
};

const clarificationRequest: ClarificationRequestItem = {
  id: generateTimelineId(),
  type: 'clarification_request',
  createdAt: getTimeOffset(1.5),
  roundNumber: 1,
  requestId: 'test-clarification-1',
  questions: [
    {
      question: 'Jaki jest główny cel Twojego zatrudnienia?',
      options: [
        { label: 'Stabilność i długoterminowe zatrudnienie', description: 'Preferuję stałe zatrudnienie z pełnymi prawami pracowniczymi' },
        { label: 'Elastyczność i praca projektowa', description: 'Wolę pracować na konkretne projekty bez stałego zobowiązania' },
        { label: 'Maksymalizacja dochodu netto', description: 'Najważniejsza jest dla mnie kwota "na rękę"' },
      ],
      allowOther: true,
    },
    {
      question: 'Czy zależy Ci na ubezpieczeniach społecznych (emerytalne, chorobowe)?',
      options: [
        { label: 'Tak, to dla mnie priorytet', description: 'Chcę mieć pełne ubezpieczenie społeczne' },
        { label: 'Częściowo, ale nie jest to kluczowe', description: 'Miło mieć, ale nie jest to decydujące' },
        { label: 'Nie, mam inne zabezpieczenia', description: 'Mam własne ubezpieczenie lub inne formy zabezpieczenia' },
      ],
      allowOther: false,
    },
  ],
  status: 'answered',
  responses: [
    { questionIndex: 0, selectedOption: 'Stabilność i długoterminowe zatrudnienie' },
    { questionIndex: 1, selectedOption: 'Tak, to dla mnie priorytet' },
  ],
};

const progressBlock2: ProgressBlockItem = {
  id: generateTimelineId(),
  type: 'progress_block',
  createdAt: getTimeOffset(3),
  roundNumber: 2,
  steps: [
    { type: 'progress', message: 'Przeszukuję bazę orzeczeń sądowych...', timestamp: getTimeOffset(3), completed: true },
    { type: 'progress', message: 'Znaleziono 24 potencjalnie istotnych orzeczeń', timestamp: getTimeOffset(4), completed: true },
    { type: 'progress', message: 'Analizuję orzeczenia Sądu Najwyższego...', timestamp: getTimeOffset(5), completed: true },
    { type: 'progress', message: 'Analizuję orzeczenia sądów powszechnych...', timestamp: getTimeOffset(6), completed: true },
    { type: 'progress', message: 'Identyfikuję podstawy prawne...', timestamp: getTimeOffset(7), completed: true },
    { type: 'progress', message: 'Generuję raport końcowy...', timestamp: getTimeOffset(8), completed: true },
  ],
  phase: 'synthesis',
  isComplete: true,
  isStreaming: false,
};

const report: ReportItem = {
  id: generateTimelineId(),
  type: 'report',
  createdAt: getTimeOffset(9),
  roundNumber: 2,
  markdown: `## Streszczenie

Raport analizuje wybór między umową o pracę a umową o dzieło, opierając się na dostępnych orzeczeniach sądowych. Przeanalizowane orzeczenia wskazują na przewagę umowy o pracę w kontekście ochrony praw pracowniczych, jednakże istnieją również sytuacje, w których umowa o dzieło może być bardziej korzystna.

## Odpowiedzi na pytania użytkownika

### 1. Co wybrać umowę o pracę czy o dzieło?

Wybór między umową o pracę a umową o dzieło zależy od specyfiki zatrudnienia oraz oczekiwań stron. Umowa o pracę oferuje większą ochronę praw pracowniczych, co potwierdzają orzeczenia [VI Pa 79/17](judgment:327343) oraz [IV Pa 91/17](judgment:321664), które podkreślają korzyści związane z przywróceniem do pracy oraz ustaleniem stosunku pracy. Z drugiej strony, umowa o dzieło może być bardziej elastyczna i korzystna w przypadku projektów o jasno określonym rezultacie, jednak nie zapewnia takich samych zabezpieczeń socjalnych, co potwierdzają orzeczenia przeciwne, takie jak [IX P 440/21](judgment:480474).

## Analiza orzecznictwa

### Orzeczenia wspierające

1. **[VI Pa 79/17](judgment:327343)** (Sąd Powszechny, 2017-12-18)
   - Orzeczenie potwierdza zasadność roszczenia o przywrócenie do pracy, co wskazuje na przewagę umowy o pracę w kontekście ochrony praw pracowniczych.
   > "Rozwiązanie umowy o pracę z powodem narusza więc przepisy prawa o rozwiązywaniu umów o pracę...."

2. **[IV Pa 91/17](judgment:321664)** (Sąd Powszechny, 2017-12-18)
   - Orzeczenie ustala istnienie stosunku pracy, co jest korzystne dla osób rozważających wybór umowy o pracę.
   > "ustala, iż powoda S. B. łączył z pozwanymi stosunek pracy w okresie od dnia 5.11.2013r. do dnia 11.12.2013r...."

### Orzeczenia przeciwne

1. **[IX P 440/21](judgment:480474)** (Sąd Powszechny, 2023-01-01)
   - Orzeczenie odrzuca roszczenie o odprawę emerytalną, co wskazuje na ograniczenia umowy o pracę w kontekście wewnętrznych przepisów.
   > "Powództwo okazało się nieuzasadnione...."

2. **[IV U 317/17](judgment:321132)** (Sąd Powszechny, 2017-12-18)
   - Orzeczenie odmawia przyznania zasiłku chorobowego, co podkreśla ryzyko związane z wykonywaniem pracy zarobkowej podczas niezdolności do pracy.
   > "Powód w czasie orzeczonej niezdolności do pracy świadczył pracę zarobkową na podstawie umowy zlecenia...."

## Podstawy prawne

- **Art. 56 KP** - Dotyczy przywrócenia do pracy w przypadku niezgodnego z prawem rozwiązania umowy o pracę, co jest istotne w kontekście ochrony praw pracowniczych.
- **Art. 8 ust. 1 ustawy z dnia 13 marca 2003 roku** - Odnosi się do zasad rozwiązywania stosunków pracy z przyczyn nie dotyczących pracowników, co ma znaczenie przy wyborze formy zatrudnienia.

## Rekomendacje

- **Kroki do podjęcia:** Rozważenie specyfiki pracy i oczekiwań stron przed wyborem formy zatrudnienia. W przypadku długoterminowego zatrudnienia z przewidywaną ochroną praw pracowniczych, umowa o pracę może być bardziej korzystna.
- **Szanse powodzenia sprawy:** 70% na korzyść umowy o pracę w kontekście ochrony praw pracowniczych.
- **Potencjalne ryzyka:** Brak zabezpieczeń socjalnych w przypadku umowy o dzieło.
- **Czy warto iść do sądu:** Warto rozważyć postępowanie sądowe w przypadku niezgodnego z prawem rozwiązania umowy o pracę, co potwierdzają korzystne orzeczenia.`,
};

const followupQuestion: FollowupQuestionItem = {
  id: generateTimelineId(),
  type: 'followup_question',
  createdAt: getTimeOffset(12),
  roundNumber: 2,
  content: 'A co z umową zlecenie? Czy to lepsza opcja niż umowa o dzieło?',
};

const followupProgressBlock: ProgressBlockItem = {
  id: generateTimelineId(),
  type: 'progress_block',
  createdAt: getTimeOffset(12.5),
  roundNumber: 2,
  steps: [
    { type: 'progress', message: 'Analizuję pytanie uzupełniające...', timestamp: getTimeOffset(12.5), completed: true },
    { type: 'progress', message: 'Przeszukuję dodatkowe orzeczenia...', timestamp: getTimeOffset(13), completed: true },
  ],
  phase: 'analysis',
  isComplete: true,
  isStreaming: false,
};

const followupResponse: FollowupResponseItem = {
  id: generateTimelineId(),
  type: 'followup_response',
  createdAt: getTimeOffset(14),
  roundNumber: 2,
  content: `Umowa zlecenie stanowi pewien kompromis między umową o pracę a umową o dzieło. Oto kluczowe różnice:

**Umowa zlecenie vs umowa o dzieło:**

1. **Składki ZUS** - Umowa zlecenie podlega obowiązkowym składkom na ubezpieczenie społeczne (emerytalne, rentowe, wypadkowe) oraz zdrowotne. Umowa o dzieło co do zasady nie podlega składkom ZUS (z wyjątkiem gdy jest zawarta z własnym pracodawcą).

2. **Przedmiot umowy** - Zlecenie to zobowiązanie do starannego działania (np. świadczenie usług), dzieło to zobowiązanie do osiągnięcia konkretnego rezultatu.

3. **Ochrona prawna** - Zleceniobiorca ma pewną ochronę (minimalna stawka godzinowa od 2017r.), ale mniejszą niż pracownik. Wykonawca dzieła praktycznie nie ma ochrony.

**Porównując z poprzednią analizą:**

Z perspektywy Twoich priorytetów (stabilność + ubezpieczenia społeczne), ranking form zatrudnienia wygląda następująco:

1. **Umowa o pracę** - najlepsza ochrona i pełne ubezpieczenia
2. **Umowa zlecenie** - obowiązkowe składki ZUS, ale mniejsza stabilność
3. **Umowa o dzieło** - brak składek ZUS, najmniejsza ochrona

Orzeczenie [III AUa 501/17](judgment:321293) potwierdza, że ZUS może zakwestionować umowę o dzieło i przekwalifikować ją na zlecenie, jeśli charakter pracy na to wskazuje.`,
  referencedJudgments: ['321293'],
  tokensUsed: 1250,
  isStreaming: false,
};

export const TEST_SESSION: ConversationSession = {
  id: 'test-session-' + Date.now(),
  status: 'complete',
  timeline: [
    userInput,
    progressBlock1,
    clarificationRequest,
    progressBlock2,
    report,
    followupQuestion,
    followupProgressBlock,
    followupResponse,
  ],
  currentRound: 2,
  tokensUsed: 14580,
  tokensLimit: 50000,
  error: null,
};

export const TEST_TRIGGER = '[test]';
